import {BaseCartController} from '../domain/controllers/BaseCartController';
import {CartStore} from '../domain/stores/CartStore';
import {OrderStore} from '../domain/stores/OrderStore';
import {StyleParam} from '../common/constants';
import {
  BaseController,
  DynamicPaymentMethodsShape,
  DynamicPaymentMethodsTheme,
  ICart as ICartFromCartApi,
} from '@wix/wixstores-client-storefront-sdk';
import {CashierExpressStore} from '../domain/stores/CashierExpressStore';
import {NavigationStore} from '../domain/stores/NavigationStore';
import {CartModel} from '../domain/models/Cart/Cart.model';
import {calcTopThreeViolations} from '../domain/utils/violations.utils';
import {LineItemModel} from '../domain/models/LineItems/LineItem.model';
import {UpsellFetchStatus, UpsellOptionModel} from '../domain/models/LineItems/UpsellOption.model';
import {PurchaseRecommendationModel} from '../domain/models/PurchaseRecommendation.model';
import {CartSummaryModel} from '../domain/models/CartSummary/CartSummary.model';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {AddCouponErrorModel} from '../domain/models/AddCouponError.model';

type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

export type IControllerProps = {
  isLoading: boolean;
  cartStore: Awaited<ReturnType<CartStore['toProps']>>;
  cashierExpressStore: Awaited<ReturnType<CashierExpressStore['toProps']>>;
  generalError: boolean;
  isResponsive: boolean;
  navigationStore: Awaited<ReturnType<NavigationStore['toProps']>>;
  orderStore: Awaited<ReturnType<OrderStore['toProps']>>;
  fitToContentHeight: boolean;
};

export type ICartControllerApi = {
  updateComponent: BaseCartController['updateComponent'];
  reportFedops: BaseCartController['reportFedops'];
  reportStartFedops: BaseCartController['reportStartFedops'];
  reportEndFedops: BaseCartController['reportEndFedops'];
  t: BaseCartController['t'];
};

export enum ButtonSkins {
  BUTTON_SKIN_1 = 'button-skin-1',
  BUTTON_SKIN_2 = 'button-skin-2',
  BUTTON_SKIN_3 = 'button-skin-3',
  BUTTON_SKIN_4 = 'button-skin-4',
  BUTTON_SKIN_5 = 'button-skin-5',
  BUTTON_SKIN_6 = 'button-skin-6',
}

export type ICartStyleSettings = {
  booleans: {
    [StyleParam.ShowContinueShopping]: boolean;
    [StyleParam.ShowCoupon]: boolean;
    [StyleParam.ShowBuyerNote]: boolean;
    [StyleParam.ShowCrossSell]: boolean;
    [StyleParam.ShowExpressCheckout]: boolean;
    [StyleParam.ShowTax]: boolean;
    [StyleParam.ShowShipping]: boolean;
    [StyleParam.Responsive]: boolean;
    [StyleParam.ShowCheckoutButton]: boolean;
    [StyleParam.ShowGoToCartButton]: boolean;
    [StyleParam.CartItemQuantityCounterVisibility]: boolean;
    [StyleParam.CartItemImageVisibility]: boolean;
    [StyleParam.CartItemVisibility]: boolean;
    [StyleParam.CartItemInfo]: boolean;
    [StyleParam.CustomerInputsPromoCodeVisibility]: boolean;
    [StyleParam.CustomerInputsNoteVisibility]: boolean;
    [StyleParam.CartSummaryDisclaimerVisibility]: boolean;
    [StyleParam.CartSummarySecureBadgeVisibility]: boolean;
    [StyleParam.cartTitleVisibility]: boolean;
    [StyleParam.cartNumberOfCartItemsVisibility]: boolean;
    [StyleParam.CartSummarySubtotalVisibility]: boolean;
  };
  fonts: {
    [StyleParam.CornerRadius]: {value: string; fontStyleParam: boolean};
    [StyleParam.SelectedSkin]: {value: ButtonSkins; fontStyleParam: boolean};
  };
  numbers: {
    [StyleParam.dynamicPaymentMethodsShape]: DynamicPaymentMethodsShape;
    [StyleParam.dynamicPaymentMethodsTheme]: DynamicPaymentMethodsTheme;
    [StyleParam.CheckoutButtonsPosition]: number;
  };
};

export type IShippingRule = ICartFromCartApi['shippingRuleInfo']['shippingRule'];
export type IShippingOption = ICartFromCartApi['shippingRuleInfo']['shippingRule']['options'][0];
export type IPickupDetails = ICartFromCartApi['shippingRuleInfo']['shippingRule']['options'][0]['pickupInfo'];
export type IOptionsSelectionsValue = ICartFromCartApi['items'][0]['optionsSelectionsValues'][0];

export type VeloInputs = {
  shouldShowSecureCheckout?: boolean;
};

export type SettingsOverrides = VeloInputs;

export type ExperimentProps = {
  shouldPresentTooltipWithoutNumber: boolean;
  AddSlotToCartPage: boolean;
  sideCartElementsVisibilityInCss: boolean;
  mapCartSummaryColors: boolean;
};

export type CartStoreProps = {
  shouldRenderEmptyState?: boolean;
  loadingItems: string[];
  cartModel: CartModel;
  cartSummary: CartSummaryModel;
  itemsCount?: number;
  isNonShippableCart?: boolean;
  addCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  isCouponUpdateInProgress: boolean;
  couponError?: AddCouponErrorModel;
  isFreeCart?: boolean;
  sendToggleCouponBi: () => void;
  removeItemsFromCart: (lineItems: LineItemModel[]) => Promise<void>;
  sendAddNoteBi: () => void;
  sendMinimumOrderMessageShownBi: (buttonEnabled: boolean) => void;
  reportClickToOpenCloseMoreDetailsBi: ({lineItemId, action}: {lineItemId: string; action: string}) => void;
  reportCartButtonPresented: (isButtonShown: boolean) => void;
  reportPromoCodePresented: (isPromoCodeShown: boolean) => void;
  reportBuyerNotePresented: (isBuyerNoteShown: boolean) => void;
  reportCheckoutButtonPresented: (isButtonShown: boolean) => void;
  updateBuyerNote: (note: string) => Promise<void>;
  updateItemQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  upsellOptionsByLineItemId: Record<string, UpsellOptionModel[]>;
  upsellFetchStatus: UpsellFetchStatus;
  purchaseRecommendations: PurchaseRecommendationModel[];
  arePurchaseRecommendationsLoading: boolean;
  shouldShowCrossSell: boolean;
  applyRecommendation: (recommendation: PurchaseRecommendationModel, position: number) => Promise<void>;
  isApplyingRecommendation: boolean;
  acceptUpsell: (
    lineItem: LineItemModel,
    options: UpsellOptionModel[],
    selectedOption: UpsellOptionModel
  ) => Promise<void>;
  reportUpsellDropdownOpened: (lineItem: LineItemModel, options: UpsellOptionModel[]) => void;
  screenReaderMessage?: string;
  isCartValid: boolean;
  shouldShowCoupon: boolean;
  shouldShowBuyerNote: boolean;
  shouldShowMinimumOrderAmount: boolean;
  shouldShowTopCheckoutButtonInMobile: boolean;
  shouldDisplayViolations: boolean;
  shouldShowSecureCheckout?: boolean;
  isPartiallyPaid: boolean;
  hasErrorViolations: boolean;
  topThreeViolations?: ReturnType<typeof calcTopThreeViolations>;
  shouldShowError: boolean;
  errorOnEstimateTotals: boolean;
  clearError: () => Promise<void>;
  lineItemsLoaded: () => void;
  pageLoaded: () => void;
  isSummaryUpdating: boolean;
  isSummaryLoading: boolean;
  shouldEstimateTotals: boolean;
  refetchCartSummary: () => Promise<void>;
  successPopupLineItem?: LineItemModel | null;
} & ExperimentProps;

export enum CartLayout {
  CART = 'cart',
  SIDE_CART = 'sideCart',
  SUCCESS_POPUP = 'successPopup',
}

export enum clickOnShowMoreBi {
  OPEN = 'open',
  CLOSE = 'close',
}

export type ApplicationError = {details?: {applicationError?: {code?: string}}};

export type ModelContext = {
  viewerContext: ViewerContext;
  formatAmount: (amount: number | string) => string;
  shouldPayWithConvertedCurrency: boolean;
  baseUrl: string;
};

type AbstractConstructorHelper<T> = (new (...args: any) => {[x: string]: any}) & T;
type AbstractConstructorParameters<T> = ConstructorParameters<AbstractConstructorHelper<T>>;
export type ControllerConstructorType<T> = new (...args: AbstractConstructorParameters<BaseController>) => T;
